import { createSelector } from "@reduxjs/toolkit";
import { CustomerAccountRole, RewardsAccount, RewardsPrice } from "redmond";
import { IStoreState } from "../../../../reducers/types";
import { isCorpTenant } from "@capone/common";
import { config } from "../../../../api/config";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.premierCollectionRewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) =>
  state.premierCollectionRewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.premierCollectionRewards.rewardsAccounts;

export const getIsVentureX = (state: IStoreState) =>
  getRewardsAccounts(state).some((acc) =>
    acc.productDisplayName.includes("Venture X")
  );

export const getUserInfo = (state: IStoreState) =>
  state.premierCollectionRewards.userInfo;

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.premierCollectionRewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.premierCollectionRewards.fetchRewardsAccountsCallState;

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort((prev, current) => {
      return (
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
      );
    });
    return sortedAccts[0];
  }
);

export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);
// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | null => {
    // return the id with largest value
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return null;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null => {
    return userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId;
  }
);

export const getRewardsAccountsReferenceIds = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string[] =>
    rewardsAccounts.map((account) => account.accountReferenceId)
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined => {
    return rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    );
  }
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) => {
    return accounts.findIndex((account) => account.accountReferenceId === id);
  }
);

export const getSelectedAccountReferenceIdIfRedemptionEnabled = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, selectedAccountId) => {
    const account = accounts.find(
      (account) => account.accountReferenceId === selectedAccountId
    );

    return account?.allowRewardsRedemption ?? true
      ? selectedAccountId || undefined
      : undefined;
  }
);

// corporate
export const getIsInPolicy = (state: IStoreState) => {
  const lodging = state.premierCollectionShop.selectedLodging?.lodging;

  if (lodging && "corporateTravel" in lodging) {
    return lodging.corporateTravel.policyCompliance.isInPolicy;
  }
  return true;
};

export const getAllowRewardsRedemptionInAnyAccount = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): boolean => {
    return rewardsAccounts.some(
      (account) => account.allowRewardsRedemption ?? true
    );
  }
);

export const getIsAutoApprovalEnabled = (state: IStoreState) =>
  state.premierCollectionRewards.isAutoApprovalEnabled;

export const getAllowRewardsWithPolicy = createSelector(
  getAllowRewardsRedemptionInAnyAccount,
  (allowRewards): boolean => {
    if (isCorpTenant(config.TENANT)) {
      return allowRewards;
    }
    return true;
  }
);

export const getUserIsPrimaryCardHolder = createSelector(
  getRewardsAccounts,
  (rewardsAccounts) => {
    return rewardsAccounts.some(
      (account) => account.customerAccountRole === CustomerAccountRole.Primary
    );
  }
);

export const getRewardsAccountForBanner = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (highestEarnAccount): RewardsAccount | undefined => {
    return highestEarnAccount;
  }
);

export const getEarnForHighestEarnRewardsAccount = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (state: IStoreState) => state.premierCollectionBook.earnValuesByRewardAcctId,
  (state: IStoreState) => state.premierCollectionBook.priceQuote,
  (state: IStoreState) =>
    state.premierCollectionBook.rewardsPaymentInFiatCurrency,
  (state: IStoreState) => state.premierCollectionBook.offerToApply,
  (state: IStoreState) => state.premierCollectionBook.creditToApply,
  (state: IStoreState) =>
    state.premierCollectionBook.paymentMethodRewardsAccountId,
  (
    highestEarnRewardsAccount,
    earnValuesByRewardAcctId,
    priceQuote,
    rewardsPaymentInFiatCurrency,
    offerToApply,
    creditToApply,
    paymentMethodRewardsAccountId
  ): RewardsPrice | undefined => {
    const rewardsAccount = highestEarnRewardsAccount;

    if (!rewardsAccount) {
      return undefined;
    }

    if (rewardsAccount?.customerAccountRole !== CustomerAccountRole.Primary) {
      return undefined;
    }

    const tripTotal = priceQuote?.pricing?.tripTotal?.fiat?.value;

    if (!tripTotal) {
      const earnForAccount =
        earnValuesByRewardAcctId[rewardsAccount.accountReferenceId];

      if (!earnForAccount) {
        return undefined;
      }

      return {
        value: earnForAccount.amount,
        currency: rewardsAccount.rewardsBalance.currency,
        currencyDescription:
          rewardsAccount.rewardsBalance.currencyDescription || "miles",
      };
    }

    if (paymentMethodRewardsAccountId !== rewardsAccount.accountReferenceId) {
      return undefined;
    }

    const rewardsPayment = rewardsPaymentInFiatCurrency?.value || 0;
    const offerAmount = offerToApply?.amount?.amount || 0;
    const creditAmount = creditToApply?.amount?.amount || 0;

    const totalCreditsAndRewards = rewardsPayment + offerAmount + creditAmount;
    const creditCardPaymentRequired = Math.max(
      0,
      tripTotal - totalCreditsAndRewards
    );

    if (creditCardPaymentRequired <= 0) {
      return undefined;
    }

    const earnForAccount =
      earnValuesByRewardAcctId[rewardsAccount.accountReferenceId];

    if (!earnForAccount) {
      return undefined;
    }

    const earnAmount = earnForAccount.amount;

    return {
      value: earnAmount,
      currency: rewardsAccount.rewardsBalance.currency,
      currencyDescription:
        rewardsAccount.rewardsBalance.currencyDescription || "miles",
    };
  }
);
